import { Cell, HeadTip, makeField, NumInput, Panel, ResultLine, YNToggle } from "@/components/FieldInput";
import { ExpensesEditor } from "@/components/ExpensesEditor";
import { ASSET_ROWS, LIAB_ROWS } from "@/lib/schema";
import { money } from "@/lib/format";

const Stat = ({ label, value, tone, testId, sub }) => (
  <div className="mini-stat"><span className="metric-label">{label}</span><span className={`font-mono font-bold ${tone || "text-slate-900"}`} data-testid={testId}>{value}</span>{sub && <span className="text-[10.5px] text-slate-500">{sub}</span>}</div>
);

export const FinancialTab = ({ d, s, set, cur, req }) => {
  const f = makeField(d, set, req);
  const { ov, exp } = s;
  const A = d.financial.assets;
  const L = d.financial.liabilities;
  return (
    <div className="tab-grid">
      <Panel title="Employment & household income" testId="panel-income">
        <div className="form-grid form-grid-4">
          {f("financial.income.clientOccupation", "Client occupation")}
          {f("financial.income.clientEmployer", "Client employer")}
          {f("financial.income.clientAnnual", "Client gross / yr", "money")}
          {f("financial.income.clientTakeHome", "Client take-home / mo", "money")}
          {f("financial.income.spouseOccupation", "Spouse occupation")}
          {f("financial.income.spouseEmployer", "Spouse employer")}
          {f("financial.income.spouseAnnual", "Spouse gross / yr", "money")}
          {f("financial.income.spouseTakeHome", "Spouse take-home / mo", "money")}
          {f("financial.income.otherAnnual", "Other gross / yr", "money")}
          {f("financial.income.otherTakeHome", "Other take-home / mo", "money")}
        </div>
        <p className="text-[11px] text-slate-500 mt-2">Gross income drives insurance needs (Tab 4). Take-home pay (after tax and deductions) drives monthly cash flow.</p>
      </Panel>
      <Panel title="Monthly cash flow & net worth" testId="panel-cashflow">
        <div className="grid grid-cols-2 gap-2">
          <Stat label="Take-home income / mo" value={ov.takeHomeMonthly ? money(ov.takeHomeMonthly, cur) : "Not entered"} tone={ov.takeHomeMonthly ? undefined : "text-amber-600"} testId="fin-monthly-income" />
          <Stat label="Monthly expenses" value={money(exp.totalMonthly, cur)} testId="fin-monthly-expenses" />
          <Stat label="Debt payments / mo" value={money(ov.debtPayments, cur)} sub="mortgage is in expenses" testId="fin-other-outflows" />
          <Stat label="Monthly cash flow" value={ov.cashFlow === null ? "Enter take-home" : money(ov.cashFlow, cur)} tone={ov.cashFlow === null ? "text-amber-600" : ov.cashFlow < 0 ? "text-rose-600" : "text-emerald-600"} testId="fin-cash-flow" />
          <Stat label="Total assets" value={money(ov.totalAssets, cur)} testId="fin-total-assets" />
          <Stat label="Total liabilities" value={money(ov.totalLiabilities, cur)} testId="fin-total-liabilities" />
        </div>
        <div className="mt-2 rounded-md bg-blue-50 border border-blue-100 px-3 py-2 flex justify-between text-sm font-semibold text-slate-900">
          <span>Net worth</span><span className="font-mono" data-testid="fin-net-worth">{money(ov.netWorth, cur)}</span>
        </div>
      </Panel>
      <Panel title="Monthly expenses" className="col-span-full" testId="panel-expenses" required={req?.all.has("panel-expenses")} missing={req?.missing.has("panel-expenses")}
        right={<div className="flex items-center gap-2 text-xs text-slate-600">Inflation % <NumInput id="assumptions-inflation" decimals value={d.assumptions.inflation} onChange={(v) => set("assumptions.inflation", v)} className="w-20" /></div>}>
        <ExpensesEditor items={d.financial.expenses.items} computed={exp} cur={cur} onChange={(items) => set("financial.expenses.items", items)} />
      </Panel>
      <Panel title="Assets" testId="panel-assets">
        <div className="amt-grid exp-head"><span>Asset</span><span>Value</span><HeadTip label="At death" /></div>
        {ASSET_ROWS.map(([k, label]) => (
          <div key={k} className="amt-grid">
            <span className="text-sm text-slate-700">{label}</span>
            <NumInput id={`asset-${k}-amount`} value={A[k]?.amount} onChange={(v) => set(`financial.assets.${k}.amount`, v)} />
            <YNToggle id={`asset-${k}-use`} value={A[k]?.use} onChange={(v) => set(`financial.assets.${k}.use`, v)} />
          </div>
        ))}
        <div className="amt-grid"><span className="text-sm text-slate-700">Investments <em className="field-hint">from Tab 3</em></span><span className="read-value font-mono" data-testid="asset-investments-total">{money(ov.investmentsTotal, cur)}</span><span /></div>
        <ResultLine label="Total assets" value={ov.totalAssets} cur={cur} strong />
      </Panel>
      <Panel title="Liabilities, mortgage & debts" testId="panel-liabilities">
        <div className="liab-grid exp-head"><span>Debt</span><span>Balance</span><span>Payment / mo</span><HeadTip label="Insure" /></div>
        {LIAB_ROWS.map(([k, label]) => (
          <div key={k} className="liab-grid m-row">
            <span className="text-sm text-slate-700 m-title">{label}</span>
            <Cell l="Balance"><NumInput id={`liab-${k}-amount`} value={L[k]?.amount} onChange={(v) => set(`financial.liabilities.${k}.amount`, v)} /></Cell>
            <Cell l="Payment / mo"><NumInput id={`liab-${k}-payment`} value={L[k]?.payment} placeholder={k === "mortgage" ? "in expenses" : ""} onChange={(v) => set(`financial.liabilities.${k}.payment`, v)} /></Cell>
            <Cell l="Insure"><YNToggle id={`liab-${k}-use`} value={L[k]?.use} onChange={(v) => set(`financial.liabilities.${k}.use`, v)} /></Cell>
          </div>
        ))}
        <ResultLine label="Total liabilities" value={ov.totalLiabilities} cur={cur} strong />
        <p className="text-[11px] text-slate-500 mt-1">Mortgage payment is counted in the "Mortgage / Rent" expense line, so it isn't counted twice in cash flow. Debts marked "Insure" feed the life insurance need in Tab 4.</p>
      </Panel>
    </div>
  );
};
